import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import logo from "../../assets/logo.png";

type PaymentStatusProps = {
  status: "success" | "cancelled";
};

/**
 * Public confirmation page shown after a Stripe checkout returns.
 *
 * This route is intentionally NOT wrapped in ProtectedRoute: the payer is
 * usually the client (not a logged-in tenant user), so the page must render
 * without a valid auth token. Previously the Stripe success/cancel URLs
 * pointed at the login-protected /invoices route, which bounced every payer to
 * the login screen.
 */
const PaymentStatus = ({ status }: PaymentStatusProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const invoiceNumber = searchParams.get("invoice");
  const isSuccess = status === "success";

  return (
    <div className="bg-gray-100 min-h-screen flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4 text-center">
        {/* Brand */}
        <img src={logo} alt="Logo" className="h-10 mx-auto mb-6" />

        {/* Status icon */}
        <div className="mb-6">
          {isSuccess ? (
            <div className="w-24 h-24 mx-auto bg-green-100 rounded-full flex items-center justify-center">
              <svg
                className="w-12 h-12 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          ) : (
            <div className="w-24 h-24 mx-auto bg-red-100 rounded-full flex items-center justify-center">
              <svg
                className="w-12 h-12 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
          )}
        </div>

        {/* Message */}
        {isSuccess ? (
          <>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              Payment Successful
            </h1>
            <p className="text-gray-600 mb-8">
              Thank you! Your payment
              {invoiceNumber ? ` for invoice #${invoiceNumber}` : ""} has been
              received. A confirmation has been recorded and the invoice will be
              updated shortly.
            </p>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              Payment Cancelled
            </h1>
            <p className="text-gray-600 mb-8">
              Your payment
              {invoiceNumber ? ` for invoice #${invoiceNumber}` : ""} was not
              completed. No amount has been charged. You can retry the payment
              from the original invoice link at any time.
            </p>
          </>
        )}

        {/* Action */}
        <button
          onClick={() => navigate("/login")}
          className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-6 rounded-lg transition duration-200 ease-in-out"
        >
          Go to Login
        </button>
      </div>
    </div>
  );
};

export default PaymentStatus;
